import { useState } from 'react';
import { KeyRound, UserPlus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { gauntlet, ApiError } from '../lib/api';
import { useSession } from '../store/session';
import { Button, Callout, Input } from '../components/ui';
import { cx } from '../lib/format';

type Mode = 'signin' | 'create';

export function LoginPage() {
  const signIn = useSession((s) => s.signIn);
  const [mode, setMode] = useState<Mode>('signin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The instance decides whether anyone can create an account. The server
  // enforces it regardless of what this page shows.
  const { data: status } = useQuery({
    queryKey: ['auth-status'],
    queryFn: () => gauntlet.authStatus(),
    staleTime: Infinity,
  });
  const registrationOpen = status?.registrationOpen === true;

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (mode === 'create') {
      if (password !== confirm) {
        setError('Passwords do not match.');
        return;
      }
      setBusy(true);
      try {
        const result = await gauntlet.setup(username, password);
        await signIn(result.token);
        return;
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not create the account.');
        setBusy(false);
        return;
      }
    }

    setBusy(true);
    try {
      const result = await gauntlet.login(username, password, totp || undefined);
      await signIn(result.token);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Sign-in failed.';
      // The server asks for a second factor by message; reveal the field rather
      // than making the user guess why the form was rejected.
      if (/two-factor/i.test(message)) {
        setNeedsTotp(true);
        setError(totp ? message : null);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-void px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-2 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-xl border border-brass/25 bg-brass-wash">
            <KeyRound className="text-brass" size={22} />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Gauntlet</h1>
          <p className="text-sm text-ink-faint">
            {mode === 'create' ? 'Create your wallet' : 'Sign in to your wallet'}
          </p>
        </div>

        {registrationOpen && (
          <div className="flex rounded-lg border border-hairline p-1">
            {(
              [
                ['signin', 'Sign in'],
                ['create', 'Create account'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setMode(id);
                  setError(null);
                  setNeedsTotp(false);
                }}
                className={cx(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  mode === id
                    ? 'bg-brass-wash text-brass'
                    : 'text-ink-dim hover:text-ink',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="panel space-y-4 p-6">
          <Input
            label="Username"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />

          <Input
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
            minLength={mode === 'create' ? 10 : undefined}
            required
          />

          {mode === 'create' && (
            <>
              <Input
                label="Confirm password"
                name="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
              <Callout tone="info">
                Your account creates its own wallet. The recovery phrase is shown once, at
                first wallet creation — write it down. Nobody, including this server, can
                restore it for you.
              </Callout>
            </>
          )}

          {needsTotp && (
            <Input
              label="Two-factor code"
              name="totp"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              mono
              required
              autoFocus
            />
          )}

          {error && <Callout tone="danger">{error}</Callout>}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            full
            loading={busy}
            icon={mode === 'create' ? <UserPlus size={16} /> : undefined}
          >
            {mode === 'create' ? 'Create account' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
}
